import { sanitize } from '@core/shared';
import { isBaseApiResponse, type BaseApiResponse } from '@core/swagger-client';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { ErrorComponent, Link, rootRouteId, useMatch, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export { isBaseApiResponse };

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
    const router = useRouter();
    const isRoot = useMatch({
        strict: false,
        select: (state) => state.id === rootRouteId
    });

    // Only show ugly error page for non-API errors. API errors will be handled by the ApiErrorPage component, which will show a nicer error page with i18n support.
    if (isBaseApiResponse(error)) {
        return <ApiErrorPage error={error} />;
    }

    return (
        <div className="min-w-0 flex-1 p-4 flex flex-col items-center justify-center gap-6">
            <ErrorComponent error={error} />
            <div className="flex gap-2 items-center flex-wrap">
                <button
                    onClick={() => {
                        router.invalidate();
                    }}
                    className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}
                >
                    Try Again
                </button>
                {isRoot ? (
                    <Link to="/" className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}>
                        Home
                    </Link>
                ) : (
                    <Link
                        to="/"
                        className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}
                        onClick={(e) => {
                            e.preventDefault();
                            globalThis.window.history.back();
                        }}
                    >
                        Go Back
                    </Link>
                )}
            </div>
        </div>
    );
}

// TODO: Should -really- not copy components from the ui package,
// but this is a temporary solution until we can refactor everything further
export function ApiErrorPage({ error }: { error: BaseApiResponse }) {
    const { t } = useTranslation();

    const router = useRouter();
    const isRoot = useMatch({
        strict: false,
        select: (state) => state.id === rootRouteId
    });

    let errorSubject = 'Generic.ApiErrorCode.';
    let isAccessDenied = error.statusCode === 401;
    let errorCode = error.errorCode;
    if (!errorCode) {
        let statusCode = error.statusCode;
        if (!statusCode) {
            statusCode = 500;
        }

        switch (statusCode) {
            case 409:
                errorCode = 'ConflictException';
                break;
            case 401:
                errorCode = 'AccessDeniedException';
                break;
            case 404:
                errorCode = 'NotFoundException';
                break;
            case 503:
                errorCode = 'ServiceUnavailableException';
                break;
            case 500:
                errorCode = 'Exception';
                break;
        }
    }
    errorCode ??= 'Exception';

    let errorHeaderKey = errorSubject + errorCode;
    let errorHeader = t(errorHeaderKey);
    if (errorHeader === errorHeaderKey) {
        errorCode = 'Exception';
        errorHeader = t(errorSubject + errorCode);
    }

    let errorBodyKey = errorSubject + errorCode + '.Body';
    let errorBody = t(errorBodyKey);
    if (errorBody === errorBodyKey) {
        errorBody = '';
    }

    errorHeader = sanitize(errorHeader ?? '');
    errorBody = sanitize(errorBody ?? '');

    return (
        <main className="px-4 py-8 md:px-8 lg:px-12 flex-1">
            {isAccessDenied && <AccessDenied classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />}
            {!isAccessDenied && <Warning classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />}
            <h1 className="font-accent text-3xl md:text-4xl lg:text-4xl mb-2 md:mb-6" dangerouslySetInnerHTML={{ __html: errorHeader! }}></h1>
            {errorBody && <p dangerouslySetInnerHTML={{ __html: errorBody! }}></p>}
            <div className="flex gap-2 items-center flex-wrap mt-6">
                <button
                    onClick={() => {
                        router.invalidate();
                    }}
                    className={`btn btn--primary`}
                >
                    {t('Generic.Button.TryAgain')}
                </button>
                {isRoot ? (
                    <Link to="/" className={`btn btn--outline-primary`}>
                        {t('Generic.Link.WebinarSchedule')}
                    </Link>
                ) : (
                    <Link
                        to="/"
                        className={`btn btn--outline-primary`}
                        onClick={(e) => {
                            e.preventDefault();
                            globalThis.window.history.back();
                        }}
                    >
                        {t('Generic.Button.Back')}
                    </Link>
                )}
            </div>
        </main>
    );
}

const AccessDenied = ({ classNames }: { classNames: string }) => (
    <svg className={classNames} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88" aria-hidden="true">
        <circle cx="44" cy="44" r="41.5" fill="none" strokeMiterlimit="10" strokeWidth="5" />
        <line x1="14.66" y1="14.66" x2="73.34" y2="73.34" fill="none" strokeMiterlimit="10" strokeWidth="5" />
    </svg>
);

const Warning = ({ classNames }: { classNames: string }) => (
    <svg className={classNames} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 34" aria-hidden="true">
        <g>
            <path
                className="fill-warning"
                d="M18.28,3.25c.56-.95,1.79-1.26,2.74-.7,.29,.17,.53,.41,.7,.7l16.48,27.72c.56,.95,.25,2.18-.7,2.74-.31,.18-.66,.28-1.02,.28H3.52c-1.1,0-2-.9-2-2,0-.36,.1-.71,.28-1.02L18.28,3.25Z"
            />
            <path
                className="fill-white"
                d="M20.06,25.54c.48,0,.86,.14,1.15,.42s.43,.64,.43,1.07-.15,.78-.43,1.06-.67,.42-1.15,.42-.85-.14-1.14-.42-.43-.63-.43-1.06,.15-.79,.43-1.07,.67-.42,1.14-.42Zm1.17-1.54h-2.34l-.33-9.85h3.01l-.33,9.85Z"
            />
        </g>
    </svg>
);
